import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";
import { useRealtimeTable } from "./useRealtime";
import { playComplete } from "@/lib/sounds";
import type { Item } from "@/lib/types";

const ITEMS_REALTIME_KEYS = ["items", "item-stats", "done-this-week", "project-items", "week-items"];

// Fetch today's items + carryover
export function useItems() {
  useRealtimeTable("items", ITEMS_REALTIME_KEYS);
  const { user } = useAuth();
  const today = new Date().toISOString().split("T")[0]; // YYYY-MM-DD

  return useQuery({
    queryKey: ["items", user?.id, today],
    queryFn: async () => {
      if (!user) return [];

      // Today's items + carryover (past items still active/captured) + undated items
      // Exclude subtasks (parent_id is not null) — they show under their parent
      const { data, error } = await supabase
        .from("items")
        .select("*")
        .eq("user_id", user.id)
        .in("status", ["captured", "active"])
        .is("parent_id", null)
        .or(`date_target.eq.${today},date_target.lt.${today},date_target.is.null`)
        .order("priority", { ascending: true })
        .order("sort_order", { ascending: true })
        .order("created_at", { ascending: false });

      if (error) throw error;

      // Sort by priority: high > medium > low
      const priorityOrder = { high: 0, medium: 1, low: 2 };
      return (data as Item[]).sort((a, b) => {
        const pa = priorityOrder[a.priority] ?? 1;
        const pb = priorityOrder[b.priority] ?? 1;
        if (pa !== pb) return pa - pb;
        return a.sort_order - b.sort_order;
      });
    },
    enabled: !!user,
  });
}

// Get stats: total today, carryover count
export function useItemStats() {
  const { user } = useAuth();
  const today = new Date().toISOString().split("T")[0];

  return useQuery({
    queryKey: ["item-stats", user?.id, today],
    queryFn: async () => {
      if (!user) return { total: 0, carryover: 0 };

      const { data, error } = await supabase
        .from("items")
        .select("id, date_target")
        .eq("user_id", user.id)
        .in("status", ["captured", "active"])
        .or(`date_target.eq.${today},date_target.lt.${today},date_target.is.null`);

      if (error) throw error;

      const total = data.length;
      const carryover = data.filter(
        (item) => item.date_target && item.date_target < today
      ).length;

      return { total, carryover };
    },
    enabled: !!user,
  });
}

// Create a new item
export function useCreateItem() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (input: { title: string; raw_input?: string; source?: string; project_tag?: string }) => {
      if (!user) throw new Error("Not authenticated");

      const today = new Date().toISOString().split("T")[0];
      const { data, error } = await supabase
        .from("items")
        .insert({
          user_id: user.id,
          title: input.title,
          raw_input: input.raw_input || input.title,
          source: input.source || "manual",
          date_target: today,
          status: "captured",
          priority: "medium",
          sort_order: 0,
          ...(input.project_tag ? { project_tag: input.project_tag } : {}),
        })
        .select()
        .single();

      if (error) throw error;
      return data as Item;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["items"] });
      queryClient.invalidateQueries({ queryKey: ["item-stats"] });
      queryClient.invalidateQueries({ queryKey: ["project-items"] });
      queryClient.invalidateQueries({ queryKey: ["week-items"] });
      queryClient.invalidateQueries({ queryKey: ["projects-with-stats"] });
    },
  });
}

// Update an item (generic)
export function useUpdateItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...updates }: Partial<Item> & { id: string }) => {
      const { data, error } = await supabase
        .from("items")
        .update(updates)
        .eq("id", id)
        .select()
        .single();

      if (error) throw error;
      return data as Item;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["items"] });
      queryClient.invalidateQueries({ queryKey: ["item-stats"] });
      queryClient.invalidateQueries({ queryKey: ["project-items"] });
      queryClient.invalidateQueries({ queryKey: ["week-items"] });
      queryClient.invalidateQueries({ queryKey: ["projects-with-stats"] });
    },
  });
}

// Complete an item
export function useCompleteItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id }: { id: string; xpValue?: number | null; priority?: string }) => {
      const { error } = await supabase
        .from("items")
        .update({ status: "done" })
        .eq("id", id);

      if (error) throw error;
    },
    onSuccess: () => {
      // Confetti burst
      import("canvas-confetti").then(({ default: confetti }) => {
        confetti({ particleCount: 80, spread: 70, origin: { y: 0.7 } });
      });
      playComplete();
      queryClient.invalidateQueries({ queryKey: ["items"] });
      queryClient.invalidateQueries({ queryKey: ["item-stats"] });
      queryClient.invalidateQueries({ queryKey: ["project-items"] });
      queryClient.invalidateQueries({ queryKey: ["week-items"] });
      queryClient.invalidateQueries({ queryKey: ["projects-with-stats"] });
      queryClient.invalidateQueries({ queryKey: ["done-this-week"] });
    },
  });
}

// Process brain dump via AI edge function
export function useProcessDump() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      item_id,
      raw_input,
      user_id,
    }: {
      item_id: string;
      raw_input: string;
      user_id: string;
    }) => {
      const { data, error } = await supabase.functions.invoke("process-dump", {
        body: { item_id, raw_input, user_id },
      });

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["items"] });
      queryClient.invalidateQueries({ queryKey: ["item-stats"] });
      queryClient.invalidateQueries({ queryKey: ["project-items"] });
      queryClient.invalidateQueries({ queryKey: ["week-items"] });
      queryClient.invalidateQueries({ queryKey: ["projects-with-stats"] });
    },
  });
}

// Fetch all items for a specific project
export function useProjectItems(projectTag: string) {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["project-items", user?.id, projectTag],
    queryFn: async () => {
      if (!user || !projectTag) return [];

      const { data, error } = await supabase
        .from("items")
        .select("*")
        .eq("user_id", user.id)
        .eq("project_tag", projectTag)
        .order("status", { ascending: true })
        .order("priority", { ascending: true })
        .order("created_at", { ascending: false });

      if (error) throw error;

      // Sort: active/captured first, then done/dismissed; within each group by priority
      const statusOrder: Record<string, number> = { captured: 0, active: 0, done: 1, dismissed: 2 };
      const priorityOrder: Record<string, number> = { high: 0, medium: 1, low: 2 };
      return (data as Item[]).sort((a, b) => {
        const sa = statusOrder[a.status] ?? 1;
        const sb = statusOrder[b.status] ?? 1;
        if (sa !== sb) return sa - sb;
        const pa = priorityOrder[a.priority] ?? 1;
        const pb = priorityOrder[b.priority] ?? 1;
        if (pa !== pb) return pa - pb;
        return 0;
      });
    },
    enabled: !!user && !!projectTag,
  });
}

// Fetch items for a given week (Mon-Sun)
export function useWeekItems(weekStart: string, weekEnd: string) {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["week-items", user?.id, weekStart, weekEnd],
    queryFn: async () => {
      if (!user) return [];

      // Get items within the week range + overdue + undated active items
      const { data, error } = await supabase
        .from("items")
        .select("*")
        .eq("user_id", user.id)
        .in("status", ["captured", "active"])
        .or(`and(date_target.gte.${weekStart},date_target.lte.${weekEnd}),date_target.lt.${weekStart},date_target.is.null`);

      if (error) throw error;

      const priorityOrder: Record<string, number> = { high: 0, medium: 1, low: 2 };
      return (data as Item[]).sort((a, b) => {
        const pa = priorityOrder[a.priority] ?? 1;
        const pb = priorityOrder[b.priority] ?? 1;
        if (pa !== pb) return pa - pb;
        return a.sort_order - b.sort_order;
      });
    },
    enabled: !!user,
  });
}

// Count items completed this week
export function useDoneThisWeek() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["done-this-week", user?.id],
    queryFn: async () => {
      if (!user) return 0;
      // Monday of current week
      const now = new Date();
      const day = now.getDay();
      const diff = day === 0 ? 6 : day - 1; // Mon=0
      const monday = new Date(now);
      monday.setDate(now.getDate() - diff);
      monday.setHours(0, 0, 0, 0);

      const { count, error } = await supabase
        .from("items")
        .select("id", { count: "exact", head: true })
        .eq("user_id", user.id)
        .eq("status", "done")
        .gte("updated_at", monday.toISOString());

      if (error) throw error;
      return count ?? 0;
    },
    enabled: !!user,
  });
}

// Snooze multiple items to a date
export function useSnoozeItems() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ ids, date_target }: { ids: string[]; date_target: string }) => {
      const { error } = await supabase
        .from("items")
        .update({ date_target })
        .in("id", ids);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["items"] });
      queryClient.invalidateQueries({ queryKey: ["item-stats"] });
      queryClient.invalidateQueries({ queryKey: ["done-this-week"] });
    },
  });
}

// Dismiss an item
export function useDismissItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("items")
        .update({ status: "dismissed" })
        .eq("id", id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["items"] });
      queryClient.invalidateQueries({ queryKey: ["item-stats"] });
      queryClient.invalidateQueries({ queryKey: ["project-items"] });
      queryClient.invalidateQueries({ queryKey: ["week-items"] });
      queryClient.invalidateQueries({ queryKey: ["projects-with-stats"] });
    },
  });
}

// Dismiss an item as "not important" — teaches the AI what to skip
export function useDismissNotImportant() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("items")
        .update({ status: "dismissed", dismissed_reason: "not_important" })
        .eq("id", id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["items"] });
      queryClient.invalidateQueries({ queryKey: ["item-stats"] });
      queryClient.invalidateQueries({ queryKey: ["project-items"] });
      queryClient.invalidateQueries({ queryKey: ["week-items"] });
      queryClient.invalidateQueries({ queryKey: ["projects-with-stats"] });
    },
  });
}
